import { createApp } from "vue";
import { createPinia } from "pinia";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

import App from "./App.vue";
import router from "./router";
import { useMainStore } from "@/stores/main.js";
import { useStyleStore } from "@/stores/style.js";
import { darkModeKey, styleKey } from "@/config.js";
import { refreshAccessToken } from "@/services";
import VueApexCharts from "vue3-apexcharts";
import "./css/main.css";
import { initAdsTag } from "@/lib/conversions.js";

initAdsTag();

/* Init Pinia */
const pinia = createPinia();

/* Create Vue app */

const app = createApp(App);

if (import.meta.env.PROD) {
  import("@sentry/vue").then((Sentry) => {
    Sentry.init({
      app,
      dsn:
        import.meta.env.VITE_SENTRY_DSN ??
        "https://6c2352ad7a9b26c6aa3556c0e2c6c161@o4511333036916736.ingest.de.sentry.io/4511578592378960",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      tracesSampleRate: Number(
        import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1,
      ),
      // Propagate trace headers to the API so frontend and backend traces link
      // up in Sentry (the backend's CORS config already allows `sentry-trace`).
      tracePropagationTargets: [
        "127.0.0.1",
        /^https:\/\/api\.buzztracker\.co\.za/,
      ],
      replaysSessionSampleRate: Number(
        import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE ?? 0,
      ),
      replaysOnErrorSampleRate: Number(
        import.meta.env.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE ?? 0.25,
      ),
    });
  });
}

app.use(pinia);
app.use(VueApexCharts);
app.use(Toast, {
  position: "top-right",
  timeout: 3000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: true,
  closeButton: "button",
  icon: true,
  rtl: false,
});

/* Init Pinia stores (before mount so the router guard can access them) */
const styleStore = useStyleStore(pinia);
const mainStore = useMainStore(pinia);

/* App style */
styleStore.setStyle(localStorage[styleKey] ?? "basic");

/* Dark mode */
if (
  (!localStorage[darkModeKey] &&
    window.matchMedia("(prefers-color-scheme: dark)").matches) ||
  localStorage[darkModeKey] === "1"
) {
  styleStore.setDarkMode(true);
}

/* Default title tag */
const defaultDocumentTitle = "BuzzTracker";

/* Rehydrate auth from JWT before mounting. Try refreshing the access token
   first, then hydrate the session. hydrateSession prefers the persisted
   workspace (localStorage) over the first organisation, so a reload or deep
   link keeps the user's active workspace. */
try {
  await refreshAccessToken();
  await mainStore.hydrateSession();
} catch (e) {
  // Session expired or not logged in — leave store unauthenticated
}

/* Set document title from route meta */
router.afterEach((to) => {
  document.title = to.meta?.title
    ? `${to.meta.title} — ${defaultDocumentTitle}`
    : defaultDocumentTitle;
});

router.beforeEach((to, from, next) => {
  const isAuthenticated = mainStore.userEmail !== null;
  const isActivated =
    mainStore.userActivated !== null && mainStore.userActivated;

  if (to.fullPath === "/awaiting-activation") {
    next();
    return;
  }

  // A newly authenticated signup has no workspace yet and must be able to
  // resume the wizard after OAuth or a refresh. Established users still land
  // on their dashboard instead of entering onboarding again.
  const hasWorkspace = mainStore.userOrganisations.length > 0;
  if (to.name === "onboarding" && isAuthenticated && hasWorkspace) {
    next({ name: "dashboard" });
    return;
  }

  const requiresAuth = to.meta.requiresAuth;

  if (requiresAuth && !isAuthenticated) {
    next("/login");
  } else {
    if (isAuthenticated && !isActivated) {
      next("/awaiting-activation");
      return;
    }
    const WRITE_ONLY_ROUTES = [
      "configuration",
      "channels",
      "locations",
      "team",
      "profile",
    ];
    if (mainStore.isReadOnly && WRITE_ONLY_ROUTES.includes(to.name)) {
      return next({ name: "dashboard" });
    }
    if (to.meta.requiresSuperuser && !mainStore.isSuperuser) {
      return next({ name: "dashboard" });
    }
    // Branch comparison is only meaningful for companies with >1 branch.
    if (to.meta.requiresBranches && !mainStore.hasMultipleBranches) {
      return next({ name: "dashboard" });
    }
    next();
  }
});

/* Mount after rehydration so the first navigation has auth state */
app.use(router);
app.mount("#app");
